import type {
  RowState,
  ColState,
  CellState,
  CellRole,
  RowGroup,
  CellIdentifier,
  CellScope,
} from "./types";

export const checkSelectionIsRectangular = (cells: CellState[]): boolean => {
  const selectedCells = cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return false;
  if (selectedCells.length === 1) return true;

  const minRow = Math.min(...selectedCells.map((cell) => cell.originRow));
  const maxRow = Math.max(
    ...selectedCells.map((cell) => cell.originRow + cell.rowSpan - 1),
  );
  const minCol = Math.min(...selectedCells.map((cell) => cell.originCol));
  const maxCol = Math.max(
    ...selectedCells.map((cell) => cell.originCol + cell.colSpan - 1),
  );

  const rectangleWidth = maxCol - minCol + 1;
  const rectangleHeight = maxRow - minRow + 1;
  const rectangleArea = rectangleWidth * rectangleHeight;

  const selectedArea = selectedCells.reduce(
    (acc, cell) => acc + cell.rowSpan * cell.colSpan,
    0,
  );

  return rectangleArea === selectedArea;
};

export const checkSingleCellIsSelected = (cells: CellState[]): boolean => {
  return cells.filter((cell) => cell.selected).length === 1;
};

export const getSoleRoleOfSelectedCells = (
  cells: CellState[],
): CellRole | undefined => {
  const selectedCells = cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return undefined;

  const soleRole = selectedCells[0].role;

  for (const cell of selectedCells) {
    if (cell.role !== soleRole) return undefined;
  }

  return soleRole;
};

export const getSoleScopeOfSelectedCells = (
  cells: CellState[],
): CellScope | undefined => {
  const selectedCells = cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return undefined;

  const soleScope = selectedCells[0].scope;

  for (const cell of selectedCells) {
    if (cell.scope !== soleScope) return undefined;
  }

  return soleScope;
};

export const getSoleSelectedGroup = (
  cells: CellState[],
  rows: RowState[],
): RowGroup | undefined => {
  const selectedCells = cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return undefined;

  const selectedGroups = selectedCells.map(
    (cell) => rows[cell.originRow].group,
  );

  const soleGroup = selectedGroups[0];

  for (const group of selectedGroups) {
    if (group !== soleGroup) return undefined;
  }

  return soleGroup;
};

export const checkSelectionIsFullWidth = (
  cells: CellState[],
  cols: ColState[],
): boolean => {
  const selectedCells = cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return false;

  const minCol = Math.min(...selectedCells.map((cell) => cell.originCol));
  const maxCol = Math.max(
    ...selectedCells.map((cell) => cell.originCol + cell.colSpan - 1),
  );

  return minCol === 0 && maxCol === cols.length - 1;
};

export const checkSelectionIsFullHeight = (
  cells: CellState[],
  rows: RowState[],
): boolean => {
  const selectedCells = cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return false;

  const minRow = Math.min(...selectedCells.map((cell) => cell.originRow));
  const maxRow = Math.max(
    ...selectedCells.map((cell) => cell.originRow + cell.rowSpan - 1),
  );

  return minRow === 0 && maxRow === rows.length - 1;
};

export const checkTopRowIsSelected = (cells: CellState[]): boolean => {
  const selectedCells = cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return false;

  const minRow = Math.min(...selectedCells.map((cell) => cell.originRow));

  return minRow === 0;
};

export const checkBottomRowIsSelected = (
  cells: CellState[],
  rows: RowState[],
): boolean => {
  const selectedCells = cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return false;

  const maxRow = Math.max(
    ...selectedCells.map((cell) => cell.originRow + cell.rowSpan - 1),
  );

  return maxRow === rows.length - 1;
};

export const checkSelectedCellsAreAtomic = (cells: CellState[]): boolean => {
  const selectedCells = cells.filter((cell) => cell.selected);
  return !selectedCells.some((cell) => cell.rowSpan > 1 || cell.colSpan > 1);
};

export const getCommonHeadersOfSelectedCells = (
  cells: CellState[],
): CellIdentifier[] => {
  const selectedCells = cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return [];

  return selectedCells.reduce((acc, cell) => {
    return acc.filter((header) => {
      return (cell.headers ?? []).some((cellHeader) => {
        return (
          cellHeader.originCol === header.originCol &&
          cellHeader.originRow === header.originRow
        );
      });
    });
  }, selectedCells[0].headers ?? []);
};

export const checkSelectionSpansSingleRow = (cells: CellState[]): boolean => {
  const selectedCells = cells.filter((cell) => cell.selected);

  const spannedRowIndexes = Array.from(
    new Set(
      selectedCells.reduce((acc, cell) => {
        const rowIndexes = Array.from(
          { length: cell.rowSpan },
          (_, index) => cell.originRow + index,
        );
        return acc.concat(rowIndexes);
      }, [] as number[]),
    ),
  );

  return spannedRowIndexes.length === 1;
};

export const checkSelectionSpansSingleCol = (cells: CellState[]): boolean => {
  const selectedCells = cells.filter((cell) => cell.selected);

  const spannedColIndexes = Array.from(
    new Set(
      selectedCells.reduce((acc, cell) => {
        const colIndexes = Array.from(
          { length: cell.colSpan },
          (_, index) => cell.originCol + index,
        );
        return acc.concat(colIndexes);
      }, [] as number[]),
    ),
  );

  return spannedColIndexes.length === 1;
};
